import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, tokens, type Me } from "./api";

/**
 * Authentication state.
 *
 * The signed-in user is fetched from `/auth/me` on load rather than decoded
 * from the token: a role changed by an administrator must take effect on the
 * next page load, not when the eight-hour token expires.
 */

type AuthState = {
  user: Me | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!tokens.access()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<Me>("/auth/me"));
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: Me;
    }>("/auth/login", { email, password });
    tokens.set(result.accessToken, result.refreshToken);
    setUser(result.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Signing out locally must succeed even if the server call does not,
      // otherwise a network blip strands the user in a session they asked to end.
    }
    tokens.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refreshUser: loadUser }),
    [user, loading, signIn, signOut, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

/** The signed-in user, for screens that only render when authenticated. */
export function useUser(): Me {
  const { user } = useAuth();
  if (!user) throw new Error("useUser called outside an authenticated route");
  return user;
}
